import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from "styled-components";
import { AGBModalContent, ContractPartnerLabel, GiftcardDisplay, LegalNoteAsterisk, MetaLink, } from "@/components/atoms";
import { formatNumberAsCurrencyString } from "@/utils/price/formatNumberAsCurrencyString";
import { provideIsLoadingProp, useLoadingState, } from "@/utils/context/LoadingContext";
import { Button, Headline as HeadlineComponent } from "@mm/ui";
import { ACTIONS, ACTIONS as ModalActions, useModal, } from "@/utils/context/ModalContext";
import { THEME } from "@core/theme";
import { extendURLParams } from "@/utils/fetchData";
import dataLayer from "@/helper/dataLayer";
import Datasecurity from "@/components/molecules/MetaNavigation/datasecurity";
import { getConfigByCheckoutType } from "./config";
import { getTariffBadge } from "@/utils/getTariffBadge";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { PaymentType } from "@/pages/CheckoutPage/types";
import { useCheckoutState } from "@/pages/CheckoutPage/context/CheckoutContext";
const { skeletonBase } = THEME;
const StyledOfferSummary = styled.div `
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey[7]};
  padding: 0.875rem 1.25rem 1.5rem;
  border-radius: 4px;

  ${({ disabled }) => disabled && "opacity: 0.5"};

  @media (max-width: 760px) {
    padding: 0;
    border: 0;
    border-radius: 0;
    ${({ showOnMobile }) => showOnMobile ? "display: block" : "display: none"};
  }
`;
const Headline = styled.h2.attrs(provideIsLoadingProp) `
  margin-top: 0;
  margin-bottom: 0.25rem;
  ${({ isLoading }) => isLoading && skeletonBase + "height: 2rem;"}
`;
const MetaLinks = styled.div `
  margin-top: auto;
  padding-top: 0.5rem;
  display: flex;
  gap: 0.5rem;

  @media (max-width: 760px) {
    ${({ hideOnMobile }) => hideOnMobile && "display: none;"}
  }
`;
const Section = styled.section `
  padding-bottom: ${({ divideAfter }) => (divideAfter ? "2rem" : "0")};
  border-bottom: ${({ divideAfter, theme }) => divideAfter ? "1px solid " + theme.colors.grey[7] : "0"};
  margin-bottom: ${({ divideAfter }) => (divideAfter ? "2rem" : "0")};
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 760px) {
    padding-bottom: ${({ divideAfter, divideAfterOnMobile }) => divideAfter && divideAfterOnMobile !== false ? "0.5rem" : "0"};
    border-bottom: ${({ divideAfter, divideAfterOnMobile, theme }) => divideAfter && divideAfterOnMobile !== false
    ? "1px solid " + theme.colors.grey[7]
    : "0"};
    margin-bottom: ${({ divideAfter, divideAfterOnMobile }) => divideAfter && divideAfterOnMobile !== false ? "0.5rem" : "0"};
  }
`;
const PriceList = styled.ul `
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  @media (max-width: 760px) {
    gap: 0.5rem;
  }
`;
const PriceListItemLabel = styled.div.attrs(provideIsLoadingProp) `
  ${({ isLoading }) => isLoading && skeletonBase}
`;
const PriceListItemPrice = styled.span.attrs(provideIsLoadingProp) `
  ${({ isLoading }) => isLoading && skeletonBase};

  display: flex;

  svg {
    ${({ isLoading }) => isLoading && "display: none;"};
  }
`;
const PriceListItem = styled.li.attrs(provideIsLoadingProp) `
  font-size: ${({ large }) => (large ? "1.25rem" : "1rem")};
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${PriceListItemLabel}, ${PriceListItemPrice} {
    ${({ isLoading, large }) => isLoading && large && "height: 1.5rem;"}
  }

  @media (max-width: 760px) {
    ${({ inline }) => inline &&
    `
            gap: 2rem;
        `};
  }
`;
const PriceListItemInterval = styled.span `
  font-weight: normal;
  margin-top: 0.5rem;
  font-size: 0.875rem;

  @media (max-width: 760px) {
    ${({ hideOnMobile }) => hideOnMobile && "display: none;"}
  }
`;
const HardwareItem = styled.div `
  display: flex;
  align-items: center;
  margin-top: 1rem;

  @media (max-width: 760px) {
    ${({ hideOnMobile }) => hideOnMobile && "display: none;"}
  }
`;
const TariffItem = styled.div `
  display: flex;
  align-items: center;

  @media (max-width: 760px) {
    ${({ hideOnMobile }) => hideOnMobile && "display: none;"}
  }
`;
const TariffItemDescription = styled.div ``;
const ItemImageWrapper = styled.div.attrs(provideIsLoadingProp) `
  flex: 0 0 7rem;
  width: 7rem;
  max-height: 7rem;
  margin-right: 1rem;

  ${({ isLoading }) => isLoading && skeletonBase + "height: 6rem; width: 7rem;"}
`;
const ItemImage = styled.img.attrs(provideIsLoadingProp) `
  display: block;
  width: 100%;
  height: auto;
  max-height: 9rem;
  object-fit: cover;
  object-position: center;

  ${({ isLoading }) => isLoading && "display: none;"}

  &:before {
    content: "";
    padding-top: 6rem;
  }

  @media (max-width: 760px) {
    width: 100%;
    max-height: 8rem;
  }
`;
const HardwareItemDescription = styled.div `
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const HardwareItemLabel = styled.p.attrs(provideIsLoadingProp) `
  font-size: 1.25rem;
  font-weight: bold;
  margin: 0 0 0.5rem;

  ${({ isLoading }) => isLoading && skeletonBase + "height: 1.5rem;"}
`;
const HardwareItemDetails = styled.div.attrs(provideIsLoadingProp) `
  font-size: 0.875rem;
  margin: 0 0 0.5rem;

  ${({ isLoading }) => isLoading &&
    `
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        align-items: flex-start;
    `}
  ${({ isLoading }) => isLoading && "& > * {" + skeletonBase + "}"}
`;
const TariffName = styled.h2.attrs(provideIsLoadingProp) `
  margin-bottom: 0.25rem;
  ${({ isLoading }) => isLoading && skeletonBase + "height: 2rem;"};

  @media (max-width: 760px) {
    margin-top: 0.25rem;
    ${({ hideOnMobile }) => hideOnMobile && "display: none;"}
  }
`;
const ContractTerm = styled.p.attrs(provideIsLoadingProp) `
  ${({ isLoading }) => isLoading && skeletonBase + "height: 1.25rem;"};

  @media (max-width: 760px) {
    margin: 0;
    ${({ hideOnMobile }) => hideOnMobile && "display: none;"}
  }
`;
const DeliveryTime = styled.div.attrs(provideIsLoadingProp) `
  ${({ isLoading }) => isLoading && skeletonBase + "height: 1.25rem;"};
  margin-bottom: 1rem;

  @media (max-width: 760px) {
    margin: 0;
    ${({ hideOnMobile }) => hideOnMobile && "display: none;"}
  }
`;
const DeliveryIcon = styled.i `
  margin-right: 0.25rem;
`;
const SvgButton = styled.button `
  position: relative;
  z-index: 1;
  text-decoration: none;
  border: none;
  background-color: transparent;
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  margin-left: 0.125rem;
`;
const WebIdLogo = styled.img `
  width: 20%;
  float: left;
  margin-right: 1rem;
  margin-bottom: 1rem;
`;
const ButtonWrapper = styled.div `
  width: 100%;
  display: flex;
  margin-top: 1.5rem;

  > * {
    flex: 1;
  }

  @media (max-width: 760px) {
    ${({ standalone }) => standalone && "margin-top: 0;"}
  }
`;
const ContractPartnerLabelWrapper = styled.div `
  @media (max-width: 760px) {
    margin-bottom: -1rem;
    ${({ hideOnMobile }) => hideOnMobile && "display: none;"}
  }
`;
const LegalNoteContainer = styled.div `
  margin-top: 2rem;
  & > p {
    margin-top: 1rem;
  }
  & > p:first-of-type {
    margin-top: 0;
  }
`;
export const OfferSummary = ({ coupon, currentIndex, disabled, isLastStep, legalNote, mode = "order", onSubmit, showSummaryOnMobile, submitDisabled, submitLabel, summary, type, }) => {
    const { state: { isLoading }, } = useLoadingState();
    const { dispatch } = useModal();
    const { tenant, cdnUrl } = extendURLParams();
    const { state: { paymentType }, } = useCheckoutState();
    const currentConfig = getConfigByCheckoutType(type);
    const metaLinks = (typeof currentConfig?.metaLinks === "function"
        ? currentConfig.metaLinks(dispatch)
        : currentConfig?.metaLinks) ?? [];
    // TODO: get from process.env
    const imageUriPrefix = "https://content.ekontor24.net/mediamarkt";
    if (!summary) {
        return _jsx(_Fragment, {});
    }
    const { tariff, hardware, accessories: baseAccessories, deliveryTime, deliveryPrice, } = summary;
    const accessories = baseAccessories.filter(({ type }) => type === "bonus") ?? [];
    const pkAccessories = baseAccessories.filter(({ type }) => type === "pk") ?? [];
    const submitQaName = currentIndex
        ? `button${isLastStep ? "Final" : `Step${currentIndex}`}Order`
        : "buttonAddToCart";
    const dispatchDeliveryModal = () => dispatch({
        type: ACTIONS.SET_MODAL,
        payload: {
            // TODO: refactor to DeliveryModal
            type: "generic",
            title: "Versand",
            content: (_jsxs(_Fragment, { children: [_jsx(HeadlineComponent, { variant: "h2", children: "Versand" }), _jsxs("ul", { children: [_jsx("li", { children: "Die Lieferung erfolgt innerhalb Deutschlands." }), _jsx("li", { children: "Die Lieferung erfolgt direkt nach Freigabe deines Vertrages. Die dargestellten Lieferzeiten gelten somit ab Freigabe des Vertrages." })] }), _jsx("h3", { children: "Identifikation der Personalien" }), _jsx("p", { children: "Im Rahmen deines Einkaufs in unserem Webshop ist es notwendig, eine Identifikation deiner Personalien durchzuf\u00FChren." }), _jsxs("div", { children: [_jsx(WebIdLogo, { src: `${cdnUrl}/img/mm-neu/webID.svg`, alt: "WebID Solutions" }), _jsx("h3", { children: "WebID \u2014 Online-Identifikation" }), _jsx("p", { children: "WebID ist ein Online-Identifikationsverfahren, welches dir erm\u00F6glicht, dich mit einem Endger\u00E4t mit Kamera und einem Ausweisdokument zu identifizieren. Eine Identifikation ist nur einmal notwendig, da diese dann in deinem WebID Account gespeichert wird." }), _jsx("p", { children: _jsx("b", { children: "Du kannst dich w\u00E4hrend der folgenden \u00D6ffnungszeiten bei WebID online identifizieren: Montag bis Sonntag von 7-22Uhr" }) })] })] })),
        },
    });
    const tariffBadge = currentConfig?.showTariffBadge
        ? getTariffBadge(tariff)
        : undefined;
    return (_jsxs(_Fragment, { children: [_jsxs(StyledOfferSummary, { disabled: disabled, showOnMobile: showSummaryOnMobile, children: [_jsxs(Section, { divideAfter: true, divideAfterOnMobile: mode === "order", children: [_jsx(Headline, { children: currentConfig?.tariffHeadline }), _jsx(ContractPartnerLabelWrapper, { hideOnMobile: mode === "price", children: _jsx(ContractPartnerLabel, { contractPartner: typeof currentConfig?.contractPartner === "function"
                                        ? currentConfig.contractPartner(tariff)
                                        : currentConfig?.contractPartner, onClick: () => dispatch({
                                        type: ACTIONS.SET_MODAL,
                                        payload: {
                                            type: "contractPartner",
                                            title: "Vertragspartner",
                                            content: tariff?.serviceProvider ? (tariff.info ? (_jsx("div", { dangerouslySetInnerHTML: {
                                                    __html: (typeof currentConfig?.contractPartnerModalContent ===
                                                        "function"
                                                        ? currentConfig.contractPartnerModalContent(tariff)
                                                        : currentConfig?.contractPartnerModalContent) ??
                                                        "",
                                                } })) : (_jsx("div", { dangerouslySetInnerHTML: {
                                                    __html: (typeof currentConfig?.contractPartnerModalContent ===
                                                        "function"
                                                        ? currentConfig.contractPartnerModalContent(tariff)
                                                        : currentConfig?.contractPartnerModalContent) ??
                                                        "",
                                                } }))) : undefined,
                                        },
                                    }) }) }), _jsxs(TariffItem, { hideOnMobile: mode === "price", children: [tariffBadge ? (_jsx(ItemImageWrapper, { children: _jsx(ItemImage, { "aria-hidden": "true", src: `https://content.ekontor24.net/mediamarkt/img/mm-neu/${tariffBadge}.svg`, alt: tariff.serviceProviderLabel }) })) : (tariff?.images && (_jsx(ItemImageWrapper, { children: _jsx(ItemImage, { src: `${imageUriPrefix}${(tariff?.images?.[0].png ?? tariff?.images?.[0].webp)
                                                ?.medium}`, alt: tariff?.name }) }))), _jsxs(TariffItemDescription, { children: [_jsx(TariffName, { "data-qa": "tariffName", children: tariff?.name }), _jsxs(ContractTerm, { children: ["Mindestvertragslaufzeit ", tariff.contractTerm, " ", tariff.contractTerm > 1 ? "Monate" : "Monat"] }), metaLinks && (_jsx(MetaLinks, { children: metaLinks.map(({ urlKey, ...props }, index) => {
                                                    const url = urlKey
                                                        ? tariff[urlKey]
                                                        : props.url;
                                                    tariff.details.addresses = tariff.addresses;
                                                    return (_jsx(MetaLink, { ...props, details: tariff.details, url: url }, `metaLinks-${index}`));
                                                }) }))] })] })] }), _jsx(Section, { divideAfter: currentConfig?.showDelivery ||
                            Boolean(hardware) ||
                            accessories.length > 0 ||
                            pkAccessories.length > 0 ||
                            currentConfig?.showTotalPrice, children: _jsxs(PriceList, { children: [currentConfig?.showConnectionFee && (_jsxs(PriceListItem, { children: [_jsx(PriceListItemLabel, { children: "Einmaliger Anschlusspreis" }), _jsxs(PriceListItemPrice, { "data-qa": "connectionFee", children: [formatNumberAsCurrencyString(tariff.connectionFee), tariff.legalNote && (_jsx(LegalNoteAsterisk, { legalNote: tariff.legalNote }))] })] })), _jsxs(PriceListItem, { children: [_jsxs(PriceListItemLabel, { children: ["Monatlicher Grundpreis", tariff.pricingIntervals?.intervals?.length > 1 && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsxs(PriceListItemInterval, { children: ["ab dem ", tariff.pricingIntervals.intervals[1]?.startMonth, ". Monat", " ", formatNumberAsCurrencyString(tariff.pricingIntervals.intervals[1]?.price)] })] })), !(tariff.pricingIntervals?.intervals?.length > 1) &&
                                                    tariff.monthlyPriceAddendum && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx(PriceListItemInterval, { children: tariff.monthlyPriceAddendum })] }))] }), _jsxs(PriceListItemPrice, { "data-qa": "monthlyPrice", children: [formatNumberAsCurrencyString(tariff.pricingIntervals?.intervals?.[0]?.price ??
                                                    tariff.monthlyPrice), tariff.legalNote && (_jsx(LegalNoteAsterisk, { legalNote: tariff.legalNote }))] })] })] }) }), currentConfig?.showDelivery && (deliveryTime || deliveryPrice) && (_jsxs(Section, { divideAfter: Boolean(hardware) ||
                            accessories.length > 0 ||
                            pkAccessories.length > 0 ||
                            currentConfig?.showTotalPrice, children: [_jsx(Headline, { children: "Lieferung" }), deliveryTime && (_jsxs(DeliveryTime, { hideOnMobile: mode === "price", children: [_jsx(DeliveryIcon, { className: "icon-MM-iconfont_Shipping", children: "\uD83D\uDE9A" }), _jsx("span", { style: { color: deliveryTime.color }, children: deliveryTime.customText ??
                                            `"Lieferung in ${deliveryTime.days} Werktagen"` }), _jsx(SvgButton, { type: "button", onClick: (e) => {
                                            e.stopPropagation();
                                            dispatchDeliveryModal();
                                        }, "aria-haspopup": "dialog", "aria-label": "Weitere Informationen zur Lieferzeit", children: _jsx("i", { className: "icon-MM_iconfont_Info", "aria-hidden": true }) })] })), _jsx(PriceList, { children: _jsxs(PriceListItem, { children: [_jsxs(PriceListItemLabel, { children: ["Versandkosten", _jsx("br", {}), _jsx(PriceListItemInterval, { hideOnMobile: true, children: summary.deliveryType ?? "Postbrief" })] }), _jsxs(PriceListItemPrice, { "data-qa": "deliveryPaymentPrice", children: [formatNumberAsCurrencyString(deliveryPrice?.webId), mode === "price" && (_jsx(SvgButton, { type: "button", onClick: (e) => {
                                                        e.stopPropagation();
                                                        dispatchDeliveryModal();
                                                    }, "aria-haspopup": "dialog", "aria-label": "Weitere Informationen zum Postbrief", children: _jsx("i", { className: "icon-MM_iconfont_Info", "aria-hidden": true }) }))] })] }) })] })), hardware && (_jsxs(_Fragment, { children: [_jsxs(Section, { divideAfter: true, divideAfterOnMobile: mode === "order", children: [_jsx(Headline, { children: currentConfig?.hardwareHeadline }), _jsxs(HardwareItem, { hideOnMobile: mode === "price", children: [_jsx(ItemImageWrapper, { children: _jsx(ItemImage, { src: `${imageUriPrefix}${(hardware?.extendedVariants?.[0].variant.images?.[0]
                                                        .png ??
                                                        hardware?.extendedVariants?.[0].variant.images?.[0].webp)?.medium}`, alt: hardware?.name }) }), _jsx(HardwareItemDescription, { children: _jsx(HardwareItemLabel, { "data-qa": "hardwareName", children: hardware?.name }) })] })] }), _jsx(Section, { divideAfter: true, children: _jsxs(PriceList, { children: [_jsxs(PriceListItem, { children: [_jsx(PriceListItemLabel, { children: "Monatlicher Grundpreis" }), _jsx(PriceListItemPrice, { children: formatNumberAsCurrencyString(hardware?.extendedVariants?.[0].variant.price) })] }), hardware?.extendedVariants?.[0].variant.deliveryPrice && (_jsxs(PriceListItem, { children: [_jsx(PriceListItemLabel, { children: "Einmalige Versandkosten" }), _jsx(PriceListItemPrice, { children: formatNumberAsCurrencyString(hardware?.extendedVariants?.[0].variant.deliveryPrice) })] }))] }) })] })), accessories.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(Section, { divideAfter: true, divideAfterOnMobile: mode === "order", children: [_jsxs(Headline, { children: ["Deine ", accessories.length > 1 ? "Zugaben" : "Zugabe"] }), _jsx(ContractPartnerLabelWrapper, { hideOnMobile: mode === "price", children: _jsx(ContractPartnerLabel, { contractPartner: "S-KON eKontor 24 GmbH", onClick: () => dispatch({
                                                type: ACTIONS.SET_MODAL,
                                                payload: {
                                                    type: "contractPartner",
                                                    title: "Details zum Vertragspartner",
                                                    content: summary?.accessories[0].info ? (_jsx("div", { dangerouslySetInnerHTML: {
                                                            __html: summary?.accessories[0].info,
                                                        } })) : undefined,
                                                },
                                            }) }) }), accessories.map((accessory) => {
                                        const image = (accessory?.extendedVariants?.[0].variant.images?.[0].png ??
                                            accessory?.extendedVariants?.[0].variant.images?.[0].webp)?.medium;
                                        return (_jsxs(HardwareItem, { hideOnMobile: mode === "price", children: [image && (_jsx(ItemImageWrapper, { children: _jsx(ItemImage, { src: `${imageUriPrefix}${image}`, alt: accessory?.name }) })), _jsxs(HardwareItemDescription, { children: [_jsx(HardwareItemLabel, { "data-qa": "accessoryName", children: accessory?.name }), _jsxs(HardwareItemDetails, { children: [_jsx("span", { children: "Erh\u00E4ltlich nach Hochladen der ersten Rechnung auf" }), " ", tenant === "mediamarkt" ? (_jsx("a", { href: "https://vorteile.mediamarkt.de", target: "_blank", rel: "noreferrer", children: "vorteile.mediamarkt.de" })) : (_jsx("a", { href: "https://vorteile.saturn.de", target: "_blank", rel: "noreferrer", children: "vorteile.saturn.de" }))] }), _jsxs(MetaLinks, { children: [_jsx(MetaLink, { details: accessory.details, modalContent: summary?.reward?.redemptionInstruction, text: "Einl\u00F6sebedingungen", onClick: (details, content) => {
                                                                        dispatch({
                                                                            type: ModalActions.SET_MODAL,
                                                                            payload: {
                                                                                title: "Einlösebedingungen",
                                                                                type: "redemptionInstruction",
                                                                                content: content ? (_jsx("div", { dangerouslySetInnerHTML: {
                                                                                        __html: content,
                                                                                    } })) : undefined,
                                                                            },
                                                                        });
                                                                        dataLayer({
                                                                            event: "gaEvent",
                                                                            eventCategory: "Details",
                                                                            eventAction: "click",
                                                                            eventLabel: "Summary:Prämie:Einlösebedingungen",
                                                                        });
                                                                    } }), _jsx(MetaLink, { details: accessory.details, text: "Pr\u00E4miendetails", onClick: (details) => {
                                                                        dispatch({
                                                                            type: ModalActions.SET_MODAL,
                                                                            payload: {
                                                                                details,
                                                                                title: "Prämiendetails",
                                                                                type: "accessoryDetails",
                                                                            },
                                                                        });
                                                                        dataLayer({
                                                                            event: "gaEvent",
                                                                            eventCategory: "Details",
                                                                            eventAction: "click",
                                                                            eventLabel: "Summary:Prämie:Prämiendetails",
                                                                        });
                                                                    } })] })] })] }, accessory.id));
                                    })] }), _jsx(Section, { divideAfter: true, children: _jsx(PriceList, { children: accessories.map((accessory) => (_jsxs(PriceListItem, { children: [_jsx(PriceListItemLabel, { children: "Einmaliger Zugabenpreis" }), _jsx(PriceListItemPrice, { "data-qa": "oneTimeAccessoryPrice", children: formatNumberAsCurrencyString(accessory?.extendedVariants?.[0].variant.price) })] }, accessory.id))) }) })] })), pkAccessories.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(Section, { divideAfter: true, divideAfterOnMobile: mode === "order", children: [_jsxs(Headline, { children: ["Deine ", pkAccessories.length > 1 ? "Prämien" : "Prämie"] }), _jsx(ContractPartnerLabelWrapper, { hideOnMobile: mode === "price", children: _jsx(ContractPartnerLabel, { contractPartner: "S-KON eKontor 24 GmbH", onClick: () => dispatch({
                                                type: ACTIONS.SET_MODAL,
                                                payload: {
                                                    type: "contractPartner",
                                                    title: "Details zum Vertragspartner",
                                                    content: pkAccessories[0].info ? (_jsx("div", { dangerouslySetInnerHTML: {
                                                            __html: pkAccessories[0].info,
                                                        } })) : undefined,
                                                },
                                            }) }) }), pkAccessories.map((accessory) => {
                                        const image = (accessory?.extendedVariants?.[0].variant.images?.[0].png ??
                                            accessory?.extendedVariants?.[0].variant.images?.[0].webp)?.medium;
                                        return (_jsxs(HardwareItem, { hideOnMobile: mode === "price", children: [image && (_jsx(ItemImageWrapper, { children: _jsx(ItemImage, { src: `${imageUriPrefix}${image}`, alt: accessory?.name }) })), _jsxs(HardwareItemDescription, { children: [_jsx(HardwareItemLabel, { "data-qa": "pkAccessoryName", children: accessory?.name }), _jsx(HardwareItemDetails, { children: summary.reward ? (_jsxs(_Fragment, { children: [summary?.reward?.redemptionInstructionShort && (_jsx("div", { dangerouslySetInnerHTML: {
                                                                            __html: summary.reward.redemptionInstructionShort,
                                                                        } })), !summary.reward.redemptionInstructionShort && (_jsx("span", { children: "Bitte w\u00E4hle einen PK Text aus" }))] })) : (_jsxs(_Fragment, { children: [_jsx("span", { children: "Erh\u00E4ltlich nach Hochladen der ersten Rechnung auf" }), " ", _jsx("a", { href: "https://vorteile.mediamarkt.de", target: "_blank", rel: "noreferrer", children: "vorteile.mediamarkt.de" })] })) }), _jsxs(MetaLinks, { children: [_jsx(MetaLink, { details: accessory.details, modalContent: summary?.reward?.redemptionInstruction, text: "Einl\u00F6sebedingungen", onClick: (details, content) => {
                                                                        dispatch({
                                                                            type: ModalActions.SET_MODAL,
                                                                            payload: {
                                                                                title: "Einlösebedingungen",
                                                                                type: "redemptionInstruction",
                                                                                content: content ? (_jsx("div", { dangerouslySetInnerHTML: {
                                                                                        __html: content,
                                                                                    } })) : undefined,
                                                                            },
                                                                        });
                                                                        dataLayer({
                                                                            event: "gaEvent",
                                                                            eventCategory: "Details",
                                                                            eventAction: "click",
                                                                            eventLabel: "Summary:Prämie:Einlösebedingungen",
                                                                        });
                                                                    } }), _jsx(MetaLink, { details: accessory.details, text: "Pr\u00E4miendetails", onClick: (details) => {
                                                                        dispatch({
                                                                            type: ModalActions.SET_MODAL,
                                                                            payload: {
                                                                                details,
                                                                                title: "Prämiendetails",
                                                                                type: "accessoryDetails",
                                                                            },
                                                                        });
                                                                        dataLayer({
                                                                            event: "gaEvent",
                                                                            eventCategory: "Details",
                                                                            eventAction: "click",
                                                                            eventLabel: "Summary:Prämie:Prämiendetails",
                                                                        });
                                                                    } })] })] })] }, accessory.id));
                                    })] }), _jsx(Section, { divideAfter: true, children: _jsx(PriceList, { children: pkAccessories.map((accessory) => (_jsxs(PriceListItem, { children: [_jsx(PriceListItemLabel, { children: "Einmaliger Pr\u00E4mienpreis" }), _jsx(PriceListItemPrice, { "data-qa": "oneTimePkAccessoryPrice", children: formatNumberAsCurrencyString(accessory?.extendedVariants?.[0].variant.price) })] }, accessory.id))) }) })] })), currentConfig?.showTotalPrice && (_jsx(Section, { divideAfter: Boolean(coupon), children: _jsxs(PriceList, { children: [_jsxs(PriceListItem, { large: true, children: [_jsx(PriceListItemLabel, { children: _jsx("span", { children: "Einmalige Kosten gesamt" }) }), _jsxs(PriceListItemPrice, { "data-qa": "totalOneTimePrice", children: [formatNumberAsCurrencyString(tariff.connectionFee +
                                                    (hardware?.extendedVariants?.[0].variant.deliveryPrice ||
                                                        0) +
                                                    accessories.reduce((sum, accessory) => (sum +=
                                                        accessory?.extendedVariants?.[0].variant.price ||
                                                            0), 0)), tariff.legalNote && (_jsx(LegalNoteAsterisk, { legalNote: tariff.legalNote }))] })] }), _jsxs(PriceListItem, { large: true, children: [_jsx(PriceListItemLabel, { children: _jsx("span", { children: "Monatliche Kosten gesamt" }) }), _jsxs(PriceListItemPrice, { "data-qa": "totalMonthlyPrice", children: [formatNumberAsCurrencyString(tariff.monthlyPrice +
                                                    (hardware?.extendedVariants?.[0].variant.price || 0)), tariff.legalNote && (_jsx(LegalNoteAsterisk, { legalNote: tariff.legalNote }))] })] })] }) })), coupon && (_jsxs(Section, { children: [_jsx(Headline, { children: "Dein Coupon" }), _jsxs(HardwareItem, { children: [_jsx(ItemImageWrapper, { children: _jsx(GiftcardDisplay, { giftcard: {
                                                image: `${cdnUrl}/img/giftcard.png`,
                                                value: coupon.value,
                                            }, giftCardDisplayWidthNonPx: "7rem", giftCardDisplayTop: "0", giftCardClampMaxWidth: true, size: "xs" }) }), _jsxs(HardwareItemDescription, { children: [_jsxs(HardwareItemLabel, { children: [formatNumberAsCurrencyString(coupon.value), " Geschenk-Coupon"] }), coupon.bullets.length > 0 && (_jsx(HardwareItemDetails, { children: _jsx("ul", { children: coupon.bullets.map((bullet, index) => (_jsx("li", { children: bullet }, `coupon-bullet-${index}`))) }) })), _jsx(MetaLinks, { children: _jsx(MetaLink, { details: coupon.details, text: "Details zum Geschenk-Coupon", onClick: (details) => {
                                                        dispatch({
                                                            type: ModalActions.SET_MODAL,
                                                            payload: {
                                                                details,
                                                                meta: {
                                                                    coupon: coupon,
                                                                },
                                                                title: "Details zum Geschenk-Coupon",
                                                                type: "giftCardDetails",
                                                            },
                                                        });
                                                        dataLayer({
                                                            event: "gaEvent",
                                                            eventCategory: "Details",
                                                            eventAction: "click",
                                                            eventLabel: "Summary:Coupoon:Details zum Geschenk-Coupon",
                                                        });
                                                    } }) })] })] })] })), legalNote && (_jsx(LegalNoteContainer, { children: typeof legalNote === "string" ? (_jsx("p", { children: legalNote })) : typeof legalNote === "function" ? (legalNote(tariff.pibUrl ?? "", 
                        // TODO: needs to be rebuild (fetch static data)
                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                        () => {
                            dispatch({
                                type: ModalActions.SET_MODAL,
                                payload: {
                                    type: "generic",
                                    title: "Allgemeine Geschäftsbedingungen",
                                    content: _jsx(AGBModalContent, {}),
                                },
                            });
                        }, () => {
                            dispatch({
                                type: ModalActions.SET_MODAL,
                                payload: {
                                    type: "generic",
                                    title: "Datenschutzerklärung",
                                    content: _jsx(Datasecurity, {}),
                                },
                            });
                        })) : (legalNote) }))] }), _jsx(ButtonWrapper, { "data-tk-cobrowse-hidden": "true", standalone: !showSummaryOnMobile, children: isLastStep && paymentType === PaymentType.Paypal ? (_jsx(PayPalButtons, { disabled: submitDisabled, fundingSource: "paypal", createOrder: async (data, actions) => {
                        const paypalOrder = await onSubmit();
                        if (paypalOrder) {
                            return actions.order.create(paypalOrder);
                        }
                        return "";
                    }, onApprove: async (data, actions) => {
                        const paypalOrder = await actions.order?.get();
                        onSubmit(paypalOrder?.id);
                    }, onCancel: () => {
                        // TODO: navigate to step with PaymentType
                    }, onError: () => {
                        // TODO: respond to paypal error
                    }, "data-qa": submitQaName })) : (_jsx(Button, { onClick: () => {
                        onSubmit();
                    }, loading: isLoading, disabled: submitDisabled, qaName: submitQaName, children: submitLabel ?? "Weiter" })) })] }));
};
